import { apiCall } from '../api';
import { getUser } from '../auth';

interface UserPreferences {
  display_name: string;
  default_timezone: string;
}

// Shoelace component interfaces
interface SlDialog extends HTMLElement {
  label: string;
  show: () => void;
  hide: () => void;
}

interface SlInput extends HTMLElement {
  value: string;
}

interface SlSelect extends HTMLElement {
  value: string;
}

interface SlButton extends HTMLElement {
  loading: boolean;
  disabled: boolean;
}

interface SlAlert extends HTMLElement {
  variant: string;
  closable: boolean;
  duration: number;
  toast: () => void;
}

// Common timezone options
const TIMEZONE_OPTIONS = [
  { value: 'America/New_York', label: 'Eastern Time (ET)' },
  { value: 'America/Chicago', label: 'Central Time (CT)' },
  { value: 'America/Denver', label: 'Mountain Time (MT)' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (PT)' },
  { value: 'America/Anchorage', label: 'Alaska Time (AKT)' },
  { value: 'Pacific/Honolulu', label: 'Hawaii Time (HT)' },
  { value: 'UTC', label: 'UTC' },
  { value: 'Europe/London', label: 'London (GMT/BST)' },
  { value: 'Europe/Paris', label: 'Paris (CET/CEST)' },
  { value: 'Europe/Berlin', label: 'Berlin (CET/CEST)' },
  { value: 'Asia/Tokyo', label: 'Tokyo (JST)' },
  { value: 'Asia/Shanghai', label: 'Shanghai (CST)' },
  { value: 'Asia/Singapore', label: 'Singapore (SGT)' },
  { value: 'Asia/Dubai', label: 'Dubai (GST)' },
  { value: 'Australia/Sydney', label: 'Sydney (AEDT/AEST)' },
];

export function createPreferencesDialog(): HTMLElement {
  const dialog = document.createElement('sl-dialog') as SlDialog;
  dialog.label = 'User Preferences';
  dialog.style.cssText = '--width: 500px;';

  dialog.innerHTML = `
    <div style="display: flex; flex-direction: column; gap: 16px;">
      <sl-input
        id="pref-display-name"
        label="Display Name"
        placeholder="Enter your display name"
        required
      ></sl-input>

      <sl-select
        id="pref-timezone"
        label="Default Timezone"
        placeholder="Select your timezone"
        hoist
      >
        ${TIMEZONE_OPTIONS.map(tz => `<sl-option value="${tz.value}">${tz.label}</sl-option>`).join('\n        ')}
      </sl-select>

      <div id="pref-error" style="display: none; color: var(--sl-color-danger-600); font-size: 0.875rem;"></div>
    </div>

    <div slot="footer" style="display: flex; gap: 8px; justify-content: flex-end;">
      <sl-button id="pref-cancel" variant="default">Cancel</sl-button>
      <sl-button id="pref-save" variant="primary">Save</sl-button>
    </div>
  `;

  // Setup event handlers
  const displayNameInput = dialog.querySelector('#pref-display-name') as SlInput;
  const timezoneSelect = dialog.querySelector('#pref-timezone') as SlSelect;
  const errorDiv = dialog.querySelector('#pref-error') as HTMLElement;
  const cancelButton = dialog.querySelector('#pref-cancel') as SlButton;
  const saveButton = dialog.querySelector('#pref-save') as SlButton;

  // Cancel handler
  cancelButton.addEventListener('click', () => {
    dialog.hide();
  });

  // Save handler
  saveButton.addEventListener('click', async () => {
    const displayName = displayNameInput.value?.trim();
    const timezone = timezoneSelect.value;

    if (!displayName) {
      showError('Display name is required');
      return;
    }

    if (!timezone) {
      showError('Timezone is required');
      return;
    }

    // Show loading state
    saveButton.loading = true;
    saveButton.disabled = true;

    try {
      const updatedPrefs = await apiCall<UserPreferences>('PUT', '/api/user-preferences/', {
        json: {
          display_name: displayName,
          default_timezone: timezone,
        },
      });

      // Clear any previous errors
      hideError();

      // Show success toast
      const toast = Object.assign(document.createElement('sl-alert'), {
        variant: 'success',
        closable: true,
        duration: 3000,
        innerHTML: `
          <sl-icon name="check-circle" slot="icon"></sl-icon>
          Preferences updated successfully
        `,
      }) as SlAlert;
      document.body.append(toast);
      toast.toast();

      // Dispatch event so other components can update
      window.dispatchEvent(new CustomEvent('preferences-updated', { detail: updatedPrefs }));

      // Close dialog
      dialog.hide();
    } catch (error) {
      console.error('Failed to update preferences:', error);
      showError('Failed to update preferences. Please try again.');
    } finally {
      saveButton.loading = false;
      saveButton.disabled = false;
    }
  });

  // Helper functions
  function showError(message: string) {
    if (errorDiv) {
      errorDiv.textContent = message;
      errorDiv.style.display = 'block';
    }
  }

  function hideError() {
    if (errorDiv) {
      errorDiv.style.display = 'none';
      errorDiv.textContent = '';
    }
  }

  // Listen for open events to load current preferences
  dialog.addEventListener('sl-show', async () => {
    hideError();

    try {
      const user = await getUser();
      if (!user) return;

      const prefs = await apiCall<UserPreferences>('GET', '/api/user-preferences/');

      if (prefs) {
        displayNameInput.value = prefs.display_name || '';
        timezoneSelect.value = prefs.default_timezone || 'America/Los_Angeles';
      }
    } catch (error) {
      console.error('Failed to load preferences:', error);
      showError('Failed to load current preferences');
    }
  });

  return dialog;
}

export function openPreferencesDialog() {
  const dialog = document.getElementById('preferences-dialog') as SlDialog | null;
  if (dialog) {
    dialog.show();
  }
}
